<script lang="ts" setup>
import {
  reactive,
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
  nextTick,
  watch,
} from "vue";
import { ElMessageBox } from "element-plus";
import type { InputInstance } from "element-plus";
import { useRoute } from "vue-router";
import { ElementUIFormRef } from "~/types/element-ui/ElementUIFormRef";
import { useSmsHistoriesStore } from "~/stores/smsHistorieStore";
import { PermissionNames } from "~/configs/permission_names";
import { useDialogStore } from "~/projects/cc/stores/dialogStore";
import { useConfigStore } from "~/stores/configStore";
import { useReplyTemplatesStore } from "~/projects/help-desk/stores/replyStore";

const configStore = useConfigStore();

const dialogStore = useDialogStore();
const replyTemplatesStore = useReplyTemplatesStore();
const smsHistoriesStore = useSmsHistoriesStore();
const sendSmsForm = ref<ElementUIFormRef | null>(null);

const formData = reactive({
  number: dialogStore.smsNumber,
  text: "",
  schedule: false,
  scheduled_at: "",
});
const sms = ref("");

const smsInfo = computed(() => dialogStore.smsCounter(formData.text));

// Warn each time the message grows into a new multi-part boundary (2 parts, then
// 3, 4, ...). Each part is billed as a separate SMS, so the user should know the
// cost is multiplying as they type. The guard prevents stacked modals.
let smsCostWarningOpen = false;
watch(
  () => smsInfo.value.segments,
  (segments, previous) => {
    if (segments >= 2 && segments > (previous ?? 0) && !smsCostWarningOpen) {
      smsCostWarningOpen = true;
      ElMessageBox.alert(
        `This message is now ${segments} parts. Cost of ${segments} SMS will be charged per SMS.`,
        "Multi-part Message",
        { type: "warning", confirmButtonText: "Got it" },
      )
        .catch(() => {})
        .finally(() => {
          smsCostWarningOpen = false;
        });
    }
  },
);

// ---- Saved-reply suggester ----
// Same idea as the inbox composer: debounce what the agent types and match it
// on the frontend against the pre-fetched reply templates (title + body), so
// there is no API call per keystroke. The SMS-specific part is that only a
// template's *text* can travel: the gateway carries no media, so a template's
// attachments are never pulled in — attachment-only templates are dropped from
// the list, and templates that have both show a note saying the files are left
// behind before the agent commits to one.
const messageInputRef = ref<InputInstance | null>(null);
const isMessageFocused = ref(false);
const templateQuery = ref("");
const templateSuggestionsDismissed = ref(false);
let templateDebounceTimer: ReturnType<typeof setTimeout> | null = null;

const canUseTemplates = computed(() =>
  isUserPermitted(PermissionNames.REPLY_TEMPLATE_SHOW),
);

// Full browse-and-pick list, same component the inbox uses, in its SMS mode.
const showTemplatePicker = ref(false);
const openTemplatePicker = () => {
  showTemplatePicker.value = true;
};

const resetTemplateSuggestions = () => {
  if (templateDebounceTimer) clearTimeout(templateDebounceTimer);
  templateQuery.value = "";
  templateSuggestionsDismissed.value = false;
  isMessageFocused.value = false;
  showTemplatePicker.value = false;
};

// The template cache is filled on demand: the inbox page loads it for the
// composer, but the SMS dialog opens from Call Histories / Unhandled Calls /
// an ongoing call, where nothing has fetched it yet. getAllTemplates() is
// idempotent, so re-opening the dialog costs nothing.
watch(
  () => dialogStore._isSmsPopUpVisible,
  (visible) => {
    if (!visible) {
      resetTemplateSuggestions();
      return;
    }
    if (canUseTemplates.value) replyTemplatesStore.getAllTemplates();
  },
  { immediate: true },
);

watch(
  () => formData.text,
  (val) => {
    if (templateDebounceTimer) clearTimeout(templateDebounceTimer);
    if (!val?.trim()) {
      // Empty / whitespace-only field -> drop the query and re-arm suggestions.
      templateQuery.value = "";
      templateSuggestionsDismissed.value = false;
      return;
    }
    templateDebounceTimer = setTimeout(() => {
      templateQuery.value = val;
    }, 250);
  },
);

onBeforeUnmount(() => {
  if (templateDebounceTimer) clearTimeout(templateDebounceTimer);
});

const templateBodies = (tpl: any): string[] =>
  Array.isArray(tpl.templates)
    ? (tpl.templates as string[]).filter((body) => body?.trim())
    : [];

const templateSuggestions = computed(() => {
  if (!canUseTemplates.value || templateSuggestionsDismissed.value) {
    return [];
  }
  const q = templateQuery.value.trim().toLowerCase();
  if (q.length < 2) return [];

  const out: {
    template: any;
    body: string;
    attachmentCount: number;
    segments: number;
    rank: number;
  }[] = [];

  for (const tpl of replyTemplatesStore.allTemplates) {
    const bodies = templateBodies(tpl);
    // An attachment-only template has nothing an SMS could carry, so it is not
    // offered at all rather than pasting an empty message.
    if (!bodies.length) continue;

    const title = (tpl.title ?? "").toLowerCase();
    const titleMatch = title.includes(q);
    const matchedBody = bodies.find((body) => body.toLowerCase().includes(q));
    if (!titleMatch && !matchedBody) continue;

    const body = matchedBody ?? bodies[0];
    out.push({
      template: tpl,
      body,
      attachmentCount:
        (tpl.gallery_items?.length ?? 0) + (tpl.attachments?.length ?? 0),
      // Templates are often long enough to bill as several SMS; show the cost
      // up front rather than after the text lands in the box.
      segments: dialogStore.smsCounter(body).segments,
      // Rank: title prefix > title contains > body contains.
      rank: title.startsWith(q) ? 0 : titleMatch ? 1 : 2,
    });
  }

  out.sort((a, b) => a.rank - b.rank);
  return out.slice(0, 6);
});

const showTemplateSuggestions = computed(
  () => isMessageFocused.value && templateSuggestions.value.length > 0,
);

const dismissTemplateSuggestions = () => {
  templateSuggestionsDismissed.value = true;
};

// Escape closes the panel first; only once it is gone does it fall through to
// Element's dialog handler and close the whole modal.
const handleMessageEscape = (event: KeyboardEvent) => {
  if (!showTemplateSuggestions.value) return;
  event.stopPropagation();
  dismissTemplateSuggestions();
};

// Body only — whatever the template had attached stays behind.
const setMessageFromTemplate = (body: string) => {
  formData.text = body;
  // The field validates on blur, so a "Message is required" error raised before
  // the pick would otherwise sit under a now-filled textarea.
  sendSmsForm.value?.clearValidate("text");
  templateSuggestionsDismissed.value = true;
  templateQuery.value = "";
  nextTick(() => messageInputRef.value?.focus());
};

const applyTemplateSuggestion = (entry: { body: string }) => {
  setMessageFromTemplate(entry.body);
};

// "Edit and Send" in the picker: the body lands in the textarea for the agent
// to adjust, and nothing is dispatched until they hit Send on this dialog.
const applyTemplateFromPicker = (template: any) => {
  const body = (template?.templates ?? []).find((text: string) => text?.trim());
  showTemplatePicker.value = false;
  if (body) setMessageFromTemplate(body);
};

onMounted(() => {
  const route = useRoute();
  const smsFromUrl = route.query.sms as string;

  if (smsFromUrl) {
    formData.number = smsFromUrl;
    dialogStore.setSmsPopUpVisibility(true);
  }
});

const rules = reactive({
  number: [{ required: true, message: "Number is required", trigger: "blur" }],
  text: [{ required: true, message: "Message is required", trigger: "blur" }],
  scheduled_at: [
    {
      validator: (rule: any, value: any, callback: any) => {
        if (formData.schedule && !value) {
          callback(
            new Error(
              "Schedule date and time is required when schedule is checked",
            ),
          );
        } else {
          callback();
        }
      },
      trigger: "blur",
    },
  ],
});

const disabledDate = (time: Date) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const oneMonthLater = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate() + 30,
    23,
    59,
    59,
  );

  return (
    time.getTime() < today.getTime() || time.getTime() > oneMonthLater.getTime()
  );
};

const disabledMinutes = () => {
  const allowedMinutes = Array.from({ length: 60 }, (_, i) => i);
  return allowedMinutes.filter((minute) => minute % 10 !== 0);
};
const roundToNearest10Minutes = (date: any) => {
  const newDate = new Date(date);
  const minutes = newDate.getMinutes();
  const roundedMinutes = Math.round(minutes / 10) * 10;
  newDate.setMinutes(roundedMinutes);
  newDate.setSeconds(0);
  newDate.setMilliseconds(0);
  return newDate;
};
const formatDateToLocal = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

const sendSms = async function () {
  if (!sendSmsForm.value) return;
  sendSmsForm.value?.validate(async (valid: boolean) => {
    if (!valid) return;

    if (formData.schedule) {
      const roundedScheduledAt = roundToNearest10Minutes(
        new Date(formData.scheduled_at),
      );
      formData.scheduled_at = formatDateToLocal(roundedScheduledAt);
    }
    const success = await dialogStore.sendSms(formData);

    if (!success) return;

    dialogStore.setSmsPopUpVisibility(false);
    await smsHistoriesStore.getSmsHistories();
    sendSmsForm?.value?.resetFields();
  });
};

const handleClose = function () {
  dialogStore.setSmsPopUpVisibility(false);
  formData.schedule = false;
  if (sendSmsForm.value) {
    sendSmsForm.value.resetFields();
  }
};
const datePicker = ref();
watchEffect(() => {
  if (formData.schedule) {
    datePicker.value?.focus();
  }
});
</script>

<template>
  <client-only>
    <el-dialog
      v-model="dialogStore._isSmsPopUpVisible"
      center
      title="Send SMS"
      :close-on-click-modal="false"
      :lock-scroll="false"
      :modal="false"
      :show-close="true"
      draggable
      width="30%"
      @close="handleClose"
    >
      <div>
        <el-form
          ref="sendSmsForm"
          :model="formData"
          :rules="rules"
          @submit.prevent="sendSms"
        >
          <div class="mt-3 rounded-md bg-gray-50 px-3 py-2">
            <div class="flex items-start gap-2">
              <Svgs
                name="info"
                class="w-4 h-4 flex-shrink-0 mt-0.5 text-blue-500"
              />

              <div class="text-xs text-gray-500 leading-5">
                <p class="font-medium text-gray-700 mb-1">
                  Number format rules
                </p>

                <ul class="list-disc pl-4 space-y-1">
                  <li>
                    Must be a valid Bangladeshi mobile number which starts with
                    <b>01</b> or <b>+8801</b>
                  </li>
                  <li>
                    If not using country code, it must be exactly 11 digits and
                    the third digit must be between <b>3 and 9</b>
                    <span class="text-gray-400"
                      >(so 011 or 012 are not allowed)</span
                    >
                  </li>
                  <li>
                    Example formats:
                    <div class="mt-1 pl-1 space-y-0.5 text-gray-700">
                      <div>• 01712345678</div>
                      <div>• +8801712345678</div>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <el-form-item prop="number">
            <FormLabel :required="true"> To</FormLabel>
            <el-input v-model="formData.number"></el-input>
          </el-form-item>
          <el-form-item class="mt-4" prop="text">
            <div class="flex w-full items-center justify-between">
              <FormLabel :required="true"> Message</FormLabel>
              <el-tooltip
                v-if="canUseTemplates"
                content="Send saved replies"
                placement="top"
              >
                <button
                  type="button"
                  class="sms-template-trigger"
                  @click="openTemplatePicker"
                >
                  <Svgs name="comment" class="w-4" />
                </button>
              </el-tooltip>
            </div>
            <div class="relative w-full">
              <el-input
                ref="messageInputRef"
                v-model="formData.text"
                type="textarea"
                @focus="isMessageFocused = true"
                @blur="isMessageFocused = false"
                @keydown.esc="handleMessageEscape"
              ></el-input>

              <!-- Saved-reply suggestions. mousedown is prevented on the panel
                   so clicking an entry never blurs the textarea out from under
                   the click and hides the list before it registers. -->
              <div
                v-if="showTemplateSuggestions"
                class="template-suggest"
                @mousedown.prevent
              >
                <div class="template-suggest-header">
                  <span>Saved replies</span>
                  <button
                    type="button"
                    class="template-suggest-close"
                    @click="dismissTemplateSuggestions"
                  >
                    <Svgs name="round-close" class="w-4" />
                  </button>
                </div>
                <div class="template-suggest-list">
                  <button
                    v-for="entry in templateSuggestions"
                    :key="entry.template.id"
                    type="button"
                    class="template-suggest-item"
                    @click="applyTemplateSuggestion(entry)"
                  >
                    <div class="template-suggest-item-top">
                      <span class="template-suggest-title truncate">
                        {{ entry.template.title }}
                      </span>
                      <span class="template-suggest-parts shrink-0">
                        {{ entry.segments }}
                        {{ entry.segments > 1 ? "parts" : "part" }}
                      </span>
                    </div>
                    <p class="template-suggest-preview">{{ entry.body }}</p>
                    <p
                      v-if="entry.attachmentCount"
                      class="template-suggest-note"
                    >
                      <Svgs name="info" class="w-3 h-3 shrink-0" />
                      {{ entry.attachmentCount }} attachment{{
                        entry.attachmentCount > 1 ? "s" : ""
                      }}
                      will not be sent with an SMS
                    </p>
                  </button>
                </div>
              </div>
            </div>
          </el-form-item>

          <div class="text-right text-xs text-gray-500 mb-2 mt-2">
            Remaining: <b>{{ smsInfo.remaining }}</b> | Parts:
            <b>{{ smsInfo.segments }}</b>
          </div>

          <!-- <div class="flex items-start gap-1">
            <Svgs name="info" class="flex-shrink-0 mt-1" />
            <span class="text-xs text-gray-400 leading-relaxed">
              Please enter a valid Bangladeshi mobile number starting with 01 or
              +8801. If you are not using the country code, the number must be
              exactly 11 digits long, and the third digit must be between 3 and
              9 (so numbers like 011 or 012 are not allowed). Example:
              01712345678 or +8801712345678
            </span>
          </div> -->
          <el-form-item class="mt-4">
            <el-checkbox
              v-if="configStore.globalConfig.sms_settings.schedule_enabled"
              v-model="formData.schedule"
              >Schedule</el-checkbox
            >
          </el-form-item>
          <el-form-item v-if="formData.schedule" prop="scheduled_at">
            <FormLabel :required="formData.schedule"> Schedule At</FormLabel>
            <el-date-picker
              ref="datePicker"
              v-model="formData.scheduled_at"
              type="datetime"
              :disabled-date="disabledDate"
              :disabled-minutes="disabledMinutes"
              placeholder="Select date and time"
              value-format="YYYY-MM-DD HH:mm:ss"
              format="YYYY-MM-DD HH:mm"
              time-format="HH:mm"
              :show-now="false"
              :editable="false"
            />
          </el-form-item>
          <div class="mt-6">
            <button
              class="focus:outline-none text-white bg-red-600 hover:bg-red-400 focus:ring-4 focus:ring-red-300 font-medium rounded-lg text-sm px-5 py-2.5 mr-2 mb-2 dark:bg-red-600 dark:hover:bg-red-700 dark:focus:ring-red-900"
              type="button"
              @click="handleClose"
            >
              Cancel
            </button>
            <button
              class="text-white bg-blue-500 hover:bg-blue-400 focus:ring-4 focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 mr-2 mb-2 dark:bg-blue-600 dark:hover:bg-blue-700 focus:outline-none dark:focus:ring-blue-800 disabled:opacity-50"
              type="submit"
              :disabled="!isUserPermitted(PermissionNames.SEND_SMS)"
              :title="
                !isUserPermitted(PermissionNames.SEND_SMS)
                  ? 'You don\'t have permission'
                  : ''
              "
            >
              Send
            </button>
          </div>
        </el-form>
      </div>
    </el-dialog>

    <!-- Saved replies browser. append-to-body keeps it out of the SMS dialog's
         own DOM (and above it); destroy-on-close means each open starts on a
         clean list rather than the previous search. -->
    <el-dialog
      v-model="showTemplatePicker"
      append-to-body
      align-center
      destroy-on-close
      width="80%"
      top="0"
      :close-on-click-modal="false"
    >
      <template #header="{ titleId, titleClass }">
        <div :id="titleId" :class="['text-md font-semibold', titleClass]">
          Saved Replies
        </div>
      </template>
      <ReplyTemplatesCollections
        :from-sms="true"
        @set-message-for-edit="applyTemplateFromPicker"
        @close-modal="showTemplatePicker = false"
      />
    </el-dialog>
  </client-only>
</template>

<style scoped>
.sms-template-trigger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  color: #5a55ce;
  transition: background-color 0.15s ease;
}
.sms-template-trigger:hover {
  background: rgba(90, 85, 206, 0.1);
}

/* Saved-reply suggester panel (drops below the message textarea) */
.template-suggest {
  position: absolute;
  left: 0;
  right: 0;
  top: calc(100% + 4px);
  z-index: 40;
  display: flex;
  flex-direction: column;
  max-height: 260px;
  overflow: hidden;
  border-radius: 12px;
  border: 1px solid rgba(148, 163, 184, 0.3);
  background: #fff;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.16);
}
.dark .template-suggest {
  background: var(--dark-blue);
  border-color: var(--dark-bluegray-border);
}
.template-suggest-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #8c89a8;
  border-bottom: 1px solid rgba(148, 163, 184, 0.2);
}
.template-suggest-close {
  display: inline-flex;
  color: #8c89a8;
  transition: color 0.15s ease;
}
.template-suggest-close:hover {
  color: #5a55ce;
}
.template-suggest-list {
  overflow-y: auto;
  overscroll-behavior: contain;
}
.template-suggest-item {
  display: block;
  width: 100%;
  text-align: left;
  padding: 8px 12px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.12);
  transition: background-color 0.15s ease;
}
.template-suggest-item:last-child {
  border-bottom: none;
}
.template-suggest-item:hover {
  background: rgba(90, 85, 206, 0.07);
}
.template-suggest-item-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.template-suggest-title {
  font-size: 13px;
  font-weight: 600;
  color: #3b3a64;
}
.dark .template-suggest-title {
  color: var(--light-font);
}
.template-suggest-parts {
  font-size: 10px;
  font-weight: 600;
  padding: 1px 8px;
  border-radius: 9999px;
  border: 1px solid #5a55ce;
  color: #5a55ce;
  background: transparent;
}
.template-suggest-preview {
  margin-top: 2px;
  font-size: 12px;
  line-height: 1.4;
  color: #6b7280;
  white-space: pre-line;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.dark .template-suggest-preview {
  color: #9ca3af;
}
.template-suggest-note {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
  font-size: 11px;
  color: #b45309;
}
.dark .template-suggest-note {
  color: #fbbf24;
}
</style>
